/**
 * Einstellungen als Menue: die erste Seite zeigt nur Kategorien (Feel,
 * Darstellung, Konto, Daten, Hilfe, Rechtliches). Die eigentlichen
 * Schalter liegen eine Ebene tiefer unter #/settings/<kategorie>.
 */
import { bookMotionOn, setBookMotion } from '../ui/book-transition.ts';
import { updateControl } from '../features/update-ui.ts';
import { storageCard } from '../features/sharing/storage-card.ts';
import type { View } from '../app.ts';
import { refresh, themeControls } from '../app.ts';
import type { Settings } from '../types.ts';
import { getSettings, saveSettings } from '../db/repo.ts';
import { AUTO_THEME_ID, THEMES, applyTheme, currentThemeId, resolveTheme, themeTone } from '../theme/themes.ts';
import {
  BACKGROUND_GROUPS,
  BACKGROUNDS,
  CHORDS,
  CUSTOM_BACKGROUND,
  DEFAULT_IMAGE_VEIL,
  INKS,
  THEME_DEFAULT,
  customArt,
  setAppearance,
} from '../theme/appearance.ts';
import { pickImageFile } from '../ui/artwork.ts';
import { shrinkImage } from '../ui/image.ts';
import { DEFAULT_UI_SCALE, UI_SCALES, applyUiScale } from '../theme/ui-scale.ts';
import type { Appearance, BackgroundGroupId, Tone } from '../theme/appearance.ts';
import { buildBackup, downloadBackup, parseBackup, pickJsonFile, restoreBackup } from '../db/backup.ts';
import { button, field, h } from '../ui/dom.ts';
import { appUrl } from '../platform/endpoints.ts';
import { navigate } from '../app.ts';
import {
  authProvider,
  can,
  currentUser,
  isMaintenanceUnlocked,
  listUsers,
  setMaintenanceUnlocked,
} from '../features/auth/session.ts';
import { ROLE_LABELS } from '../features/auth/roles.ts';
import { toast } from '../ui/toast.ts';
import { icon } from '../ui/icons.ts';
import { shapeTone } from '../theme/mix.ts';
import { LEGAL_DOCS, findLegalDoc, legalBody } from '../features/legal.ts';
import { MAX_SPEED, MIN_SPEED } from '../features/autoscroll.ts';

/** Die Kategorien der ersten Seite, in dieser Reihenfolge. */
const SECTIONS = [
  { id: 'vibe', icon: 'droplet', title: 'Feel', summary: 'Hell und Dunkel, Hintergrund, Schrift- und Akkordfarbe' },
  { id: 'darstellung', icon: 'sliders', title: 'Darstellung', summary: 'Größe der Oberfläche, Auto-Scroll, Bühnen-Modus' },
  { id: 'konto', icon: 'user', title: 'Konto', summary: 'Anmeldung, Rolle, Admin' },
  { id: 'speicher', icon: 'cloud', title: 'Speicher', summary: 'Kreisdiagramm: wie viel jedes Projekt belegt, dein Anteil' },
  { id: 'daten', icon: 'archive', title: 'Daten und Backup', summary: 'Alles sichern oder zurückholen' },
  { id: 'hilfe', icon: 'info', title: 'Hilfe und Info', summary: 'Offline, Homescreen, Zeichnen' },
  { id: 'rechtliches', icon: 'shield', title: 'Rechtliches', summary: 'Impressum, Datenschutz, Nutzungsbedingungen' },
] as const;

type SectionId = (typeof SECTIONS)[number]['id'];

/** Eine Zeile im Menue: Symbol, Titel, kurze Beschreibung, Pfeil. */
function menuRow(opts: { icon: string; title: string; summary: string; hash: string; badge?: string }): HTMLElement {
  return h(
    'button',
    { class: 'menu-row', attrs: { type: 'button' }, on: { click: () => navigate(opts.hash) } },
    h('span', { class: 'menu-row__icon' }, icon(opts.icon)),
    h(
      'span',
      { class: 'menu-row__text' },
      h('span', { class: 'menu-row__title', text: opts.title }),
      h('span', { class: 'menu-row__summary', text: opts.summary }),
    ),
    opts.badge ? h('span', { class: 'badge', text: opts.badge }) : null,
    h('span', { class: 'menu-row__chevron' }, icon('open')),
  );
}

export async function renderSettings(params: Record<string, string> = {}): Promise<View> {
  const section = SECTIONS.find((sec) => sec.id === params.section)?.id;
  if (!section) return renderMenu();
  if (section === 'rechtliches') return renderLegal(params.doc);
  return renderSection(section);
}

/** Erste Seite: nur die Kategorien. */
async function renderMenu(): Promise<View> {
  const provider = authProvider();
  const isAdmin = provider.authenticated ? await can('admin.view') : false;
  const requests = isAdmin ? (await listUsers()).filter((u) => !u.active && !u.blocked).length : 0;
  const rows = SECTIONS.map((sec) =>
    menuRow({
      icon: sec.icon,
      title: sec.id === 'konto' && !provider.authenticated ? 'Wartung' : sec.title,
      summary: sec.id === 'konto' && !provider.authenticated ? 'Systemangaben und Aufräumen' : sec.summary,
      hash: `#/settings/${sec.id}`,
      badge: sec.id === 'konto' && requests > 0 ? `${requests} neu` : undefined,
    }),
  );
  return {
    title: 'Einstellungen',
    body: h('div', { class: 'settings-menu' }, h('div', { class: 'menu-group' }, ...rows.slice(0, 2)), h('div', { class: 'menu-group' }, ...rows.slice(2, 4)), h('div', { class: 'menu-group' }, ...rows.slice(4))),
  };
}

/** Rechtliches: Liste der Dokumente oder ein Dokument. */
function renderLegal(docId: string | undefined): View {
  const doc = docId ? findLegalDoc(docId) : undefined;
  if (doc) {
    return { title: doc.title, back: '#/settings/rechtliches', body: h('section', { class: 'card' }, legalBody(doc)) };
  }
  return {
    title: 'Rechtliches',
    back: '#/settings',
    body: h(
      'div',
      { class: 'settings-menu' },
      h('div', { class: 'menu-group' }, ...LEGAL_DOCS.map((d) => menuRow({ icon: 'shield', title: d.title, summary: d.summary, hash: `#/settings/rechtliches/${d.id}` }))),
    ),
  };
}

async function renderSection(section: Exclude<SectionId, 'rechtliches'>): Promise<View> {
  let settings = await getSettings();

  async function update(patch: Partial<Settings>): Promise<void> {
    settings = { ...settings, ...patch };
    await saveSettings(settings);
  }

  /* ------------------------------------------------------------ Design */

  /** Kurzer Satz zum gewaehlten Design. */
  function describeTheme(id: string): string {
    if (id === AUTO_THEME_ID) {
      return `Richtet sich nach dem Erscheinungsbild des Geräts. Gerade aktiv: ${resolveTheme(id).name}.`;
    }
    return findDescription(id);
  }

  function findDescription(id: string): string {
    const theme = THEMES.find((t) => t.id === id);
    if (theme) return theme.description;
    return `Zwischenstufe: ${Math.round(themeTone(id) * 100)} % dunkel. Ziehen am Regler mischt Hell und Dunkel stufenlos.`;
  }

  const describe = h('p', { class: 'hint', text: describeTheme(settings.themeId) });

  /* --------------------------------- Hintergrund, Schrift, Akkorde */

  /**
   * Kacheln statt Ausklapplisten: man sieht, wofuer man sich entscheidet.
   * Jede Kachel zeigt die Fassung, die zum gerade aktiven Hell/Dunkel passt,
   * und wirkt sofort.
   */
  const bgPicker = h('div', { class: 'look' });
  const colorPicker = h('div', { class: 'look' });

  /*
   * Immer nur eine Gruppe zeigen, sonst prasseln beim Oeffnen alle Muster
   * und Farben auf einmal herein. Offen ist die Gruppe der aktuellen Wahl.
   */
  type GroupId = 'basis' | BackgroundGroupId;
  let openGroup: GroupId = BACKGROUNDS.find((b) => b.id === settings.backgroundId)?.group ?? 'basis';
  let colorTab: 'ink' | 'chord' = 'ink';

  /** Reihe von Umschaltern (Gruppen, Schrift/Akkorde). */
  function chips<T extends string>(options: { id: T; label: string }[], active: T, pick: (id: T) => void): HTMLElement {
    return h(
      'div',
      { class: 'look__chips', attrs: { role: 'tablist' } },
      ...options.map((o) =>
        h('button', {
          class: 'look__chip' + (o.id === active ? ' look__chip--on' : ''),
          text: o.label,
          attrs: { type: 'button', role: 'tab', 'aria-selected': String(o.id === active) },
          on: { click: () => pick(o.id) },
        }),
      ),
    );
  }

  function tone(): Tone {
    return resolveTheme(settings.themeId).colorScheme;
  }

  /** Grund und Schrift des Themes, fuer die Kachel "Standard". */
  function themeBase(): { bg: string; ink: string } {
    return tone() === 'dark' ? { bg: '#262624', ink: '#f5f4ef' } : { bg: '#faf9f5', ink: '#1f1e1d' };
  }

  /** Grund und Motiv, wie sie gerade gewaehlt sind – fuer die Vorschau der Farben. */
  function chosenGround(): { color: string; image: string } {
    if (settings.backgroundId === CUSTOM_BACKGROUND && settings.customBackground) {
      return { color: themeBase().bg, image: customArt(settings.customBackground, shapeTone(themeTone(settings.themeId)), settings.imageVeil) };
    }
    const bg = BACKGROUNDS.find((b) => b.id === settings.backgroundId);
    return bg ? { color: bg[tone()].bg, image: bg[tone()].art } : { color: themeBase().bg, image: 'none' };
  }

  function chosenInk(): string {
    return INKS.find((i) => i.id === settings.inkId)?.[tone()].ink ?? themeBase().ink;
  }

  function choose(patch: Partial<Appearance>): void {
    setAppearance(patch);
    applyTheme(settings.themeId);
    void update(patch);
    paintPickers();
  }

  /**
   * Eigenes Hintergrundbild: aus Fotos waehlen, verkleinern, sofort zeigen.
   * Es wird mit den Einstellungen gespeichert (je Benutzer, auch in der
   * Cloud) – deshalb klein als JPEG, nicht das Originalfoto.
   */
  async function pickBackground(): Promise<void> {
    const file = await pickImageFile();
    if (!file) return;
    try {
      // Laengste Kante 1800 px: reicht fuer den iPad-Bildschirm.
      const image = await shrinkImage(file, 1800, 600_000);
      setAppearance({ backgroundId: CUSTOM_BACKGROUND, customImage: image });
      applyTheme(settings.themeId);
      await update({ backgroundId: CUSTOM_BACKGROUND, customBackground: image });
      paintPickers();
      toast('Hintergrundbild gesetzt');
    } catch (err) {
      console.error(err);
      toast('Das Bild konnte nicht gelesen werden.', 3000);
    }
  }

  function removeBackground(): void {
    setAppearance({ backgroundId: THEME_DEFAULT, customImage: null });
    applyTheme(settings.themeId);
    void update({ backgroundId: THEME_DEFAULT, customBackground: null });
    paintPickers();
  }

  /**
   * Wie stark das eigene Bild abgedunkelt wird: 0 = Bild pur. Hell und
   * Dunkel aendern das Bild kaum (theme/appearance.ts, customArt).
   */
  function veilControl(): HTMLElement {
    const current = settings.imageVeil ?? DEFAULT_IMAGE_VEIL;
    const value = h('span', { class: 'scrollbar__value', text: `${Math.round(current * 100)} %` });
    const slider = h('input', {
      attrs: { type: 'range', min: '0', max: '100', step: '1', 'aria-label': 'Bild abdunkeln' },
    });
    slider.value = String(Math.round(current * 100));
    let frame = 0;
    slider.addEventListener('input', () => {
      value.textContent = `${slider.value} %`;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setAppearance({ imageVeil: Number(slider.value) / 100 });
        applyTheme(settings.themeId);
      });
    });
    slider.addEventListener('change', () => void update({ imageVeil: Number(slider.value) / 100 }));
    return field(
      'Bild abdunkeln',
      h('div', { class: 'scrollbar__speed' }, slider, value),
      'Dunkelt das Bild gleichmäßig ab – mehr = ruhiger und besser lesbar. Hell und Dunkel ändern nur die Bedienelemente, nicht das Bild.',
    );
  }

  function tile(opts: {
    label: string;
    on: boolean;
    ground: { color: string; image: string };
    sample: string;
    sampleColor: string;
    sampleClass?: string;
    onPick: () => void;
  }): HTMLElement {
    const swatch = h(
      'span',
      { class: 'look__swatch' },
      h('span', { class: 'look__sample ' + (opts.sampleClass ?? ''), text: opts.sample }),
    );
    swatch.style.backgroundColor = opts.ground.color;
    swatch.style.backgroundImage = opts.ground.image;
    (swatch.firstChild as HTMLElement).style.color = opts.sampleColor;
    return h(
      'button',
      {
        class: 'look__tile' + (opts.on ? ' look__tile--on' : ''),
        attrs: { type: 'button', 'aria-pressed': String(opts.on) },
        on: { click: opts.onPick },
      },
      swatch,
      h('span', { class: 'look__name', text: opts.label }),
    );
  }

  function paintPickers(): void {
    const t = tone();
    const ground = chosenGround();
    const ink = chosenInk();

    const standardTile = tile({
      label: 'Standard',
      on: settings.backgroundId === THEME_DEFAULT,
      ground: { color: themeBase().bg, image: 'none' },
      sample: 'Aa',
      sampleColor: ink,
      onPick: () => choose({ backgroundId: THEME_DEFAULT }),
    });
    const customTile = tile({
      label: settings.customBackground ? 'Eigenes Bild' : '+ Eigenes Bild',
      on: settings.backgroundId === CUSTOM_BACKGROUND,
      ground: settings.customBackground
        ? { color: themeBase().bg, image: customArt(settings.customBackground, shapeTone(themeTone(settings.themeId)), settings.imageVeil) }
        : { color: themeBase().bg, image: 'none' },
      sample: settings.customBackground ? 'Aa' : '+',
      sampleColor: ink,
      onPick: () => {
        // Schon ein Bild da: erst einmal nur waehlen. Neu waehlen geht
        // ueber den Knopf darunter.
        if (settings.customBackground && settings.backgroundId !== CUSTOM_BACKGROUND) {
          choose({ backgroundId: CUSTOM_BACKGROUND, customImage: settings.customBackground });
        } else {
          void pickBackground();
        }
      },
    });
    const backgrounds =
      openGroup === 'basis'
        ? [standardTile, customTile]
        : BACKGROUNDS.filter((bg) => bg.group === openGroup).map((bg) =>
            tile({
              label: bg.name,
              on: settings.backgroundId === bg.id,
              ground: { color: bg[t].bg, image: bg[t].art },
              sample: 'Aa',
              sampleColor: ink,
              onPick: () => choose({ backgroundId: bg.id }),
            }),
          );
    const customRow = settings.customBackground
      ? h(
          'div',
          { style: { display: 'grid', gap: '16px' } },
          h(
            'div',
            { class: 'btn-row' },
            button('Anderes Bild wählen', () => void pickBackground(), { class: 'btn btn--small' }),
            button('Bild entfernen', () => removeBackground(), { class: 'btn btn--small' }),
          ),
          settings.backgroundId === CUSTOM_BACKGROUND ? veilControl() : null,
        )
      : null;

    const inks = [
      tile({
        label: 'Standard',
        on: settings.inkId === THEME_DEFAULT,
        ground,
        sample: 'Aa',
        sampleColor: themeBase().ink,
        sampleClass: 'look__sample--text',
        onPick: () => choose({ inkId: THEME_DEFAULT }),
      }),
      ...INKS.map((i) =>
        tile({
          label: i.name,
          on: settings.inkId === i.id,
          ground,
          sample: 'Aa',
          sampleColor: i[t].ink,
          sampleClass: 'look__sample--text',
          onPick: () => choose({ inkId: i.id }),
        }),
      ),
    ];

    const chords = [
      tile({
        label: 'Standard',
        on: settings.chordId === THEME_DEFAULT,
        ground,
        sample: 'Cmaj7',
        sampleColor: t === 'dark' ? '#e58f6d' : '#b4532e',
        sampleClass: 'look__sample--chord',
        onPick: () => choose({ chordId: THEME_DEFAULT }),
      }),
      ...CHORDS.map((c) =>
        tile({
          label: c.name,
          on: settings.chordId === c.id,
          ground,
          sample: 'Cmaj7',
          sampleColor: c.id === 'text' ? ink : c[t],
          sampleClass: 'look__sample--chord',
          onPick: () => choose({ chordId: c.id }),
        }),
      ),
    ];

    const groupOptions: { id: GroupId; label: string }[] = [
      { id: 'basis', label: 'Basis' },
      ...BACKGROUND_GROUPS.map((g) => ({ id: g.id as GroupId, label: g.name })),
    ];
    const bgParts: (Node | null)[] = [
      chips(groupOptions, openGroup, (id) => {
        openGroup = id;
        paintPickers();
      }),
      openGroup === 'white' && t === 'dark'
        ? h('p', {
            class: 'hint',
            text: 'Diese Hintergründe zeigen sich weiß im hellen Design – im dunklen sind sie ihr ruhiges Gegenstück.',
          })
        : null,
      h('div', { class: 'look__grid' }, ...backgrounds),
      openGroup === 'basis' ? customRow : null,
    ];
    bgPicker.replaceChildren(...bgParts.filter((n): n is Node => n !== null));

    colorPicker.replaceChildren(
      chips(
        [
          { id: 'ink' as const, label: 'Schrift' },
          { id: 'chord' as const, label: 'Akkorde' },
        ],
        colorTab,
        (id) => {
          colorTab = id;
          paintPickers();
        },
      ),
      h('div', { class: 'look__grid look__grid--small' }, ...(colorTab === 'ink' ? inks : chords)),
      h('p', {
        class: 'hint',
        text: `Die Vorschau zeigt die ${t === 'dark' ? 'dunkle' : 'helle'} Fassung. Jede Auswahl hat beide – beim Wechsel von Hell und Dunkel passt sie sich an.`,
      }),
    );
  }
  paintPickers();

  /*
   * Derselbe Schalter wie in der Seitenleiste (Auto/Hell/Dunkel plus
   * stufenloser Regler). Hier nur mithoeren, um Text und Kacheln
   * nachzuziehen.
   */
  const onTheme = (): void => {
    settings.themeId = currentThemeId();
    describe.textContent = describeTheme(settings.themeId);
    paintPickers();
  };
  window.addEventListener('leadsheet:theme', onTheme);

  const themeCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Hell und Dunkel' }),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'field__label', text: 'Hell oder dunkel' }),
      themeControls(),
    ),
    describe,
  );

  const backgroundCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Hintergrund' }),
    bgPicker,
  );

  const colorCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Schrift- und Akkordfarbe' }),
    colorPicker,
  );

  /* ----------------------------------------------------- Darstellung */

  const speedValue = h('span', { class: 'scrollbar__value', text: `${settings.defaultScrollSpeed} px/s` });
  const speedSlider = h('input', {
    attrs: {
      type: 'range',
      min: String(MIN_SPEED),
      max: String(MAX_SPEED),
      step: '1',
      'aria-label': 'Standard-Scrollgeschwindigkeit',
    },
  });
  speedSlider.value = String(settings.defaultScrollSpeed);
  speedSlider.addEventListener('input', () => {
    speedValue.textContent = `${speedSlider.value} px/s`;
    void update({ defaultScrollSpeed: Number(speedSlider.value) });
  });

  const wakeBtn = button(
    settings.keepAwake ? 'Bildschirm bleibt an' : 'Bildschirm darf ausgehen',
    () => {
      const next = !settings.keepAwake;
      void update({ keepAwake: next });
      wakeBtn.textContent = next ? 'Bildschirm bleibt an' : 'Bildschirm darf ausgehen';
      wakeBtn.setAttribute('aria-pressed', String(next));
    },
    { pressed: settings.keepAwake },
  );

  const bookLabel = (on: boolean) => (on ? 'Animation an' : 'Animation aus');
  const bookBtn = button(
    bookLabel(bookMotionOn()),
    () => {
      const next = !bookMotionOn();
      setBookMotion(next);
      bookBtn.textContent = bookLabel(next);
      bookBtn.setAttribute('aria-pressed', String(next));
    },
    { pressed: bookMotionOn() },
  );

  /* Groesse der Oberflaeche: Segmentschalter wie Hell/Dunkel. */
  const scaleSwitch = h('div', { class: 'segmented', attrs: { role: 'group', 'aria-label': 'Schriftgröße' } });
  const currentScale = settings.uiScale ?? DEFAULT_UI_SCALE;
  const scaleButtons = UI_SCALES.map((option) => {
    const el = h('button', {
      class: 'segmented__item' + (option.value === currentScale ? ' segmented__item--on' : ''),
      text: option.label,
      attrs: { type: 'button', 'aria-pressed': String(option.value === currentScale) },
      on: {
        click: () => {
          applyUiScale(option.value);
          void update({ uiScale: option.value });
          for (const b of scaleButtons) {
            const on = b === el;
            b.classList.toggle('segmented__item--on', on);
            b.setAttribute('aria-pressed', String(on));
          }
        },
      },
    });
    scaleSwitch.append(el);
    return el;
  });

  const displayCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Darstellung und Auto-Scroll' }),
    h(
      'div',
      { class: 'field' },
      h('span', { class: 'field__label', text: 'Schriftgröße der Oberfläche' }),
      scaleSwitch,
      h('p', { class: 'hint', text: 'Gilt für Menüs, Listen und Dialoge. Das Lead Sheet hat seine eigene Größe (A− / A+).' }),
    ),
    field(
      'Standard-Geschwindigkeit',
      h('div', { class: 'scrollbar__speed' }, speedSlider, speedValue),
      'Gilt fuer Songs ohne eigene Geschwindigkeit und ohne Tempoangabe.',
    ),
    field('Buehnen-Modus', wakeBtn, 'Verhindert, dass der Bildschirm waehrend des Auftritts abschaltet.'),
    field('Projekt öffnen', bookBtn, 'Aufschlagen wie ein Buch beim Öffnen und Schließen eines Projekts. Aus: sofort in die Ansicht. Gilt für dieses Gerät.'),
  );

  /* ---------------------------------------------------------- Backup */

  const backupCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Backup' }),
    h('p', {
      class: 'hint',
      text: 'Alle Songs, Veroeffentlichungen, Setlists, die Band-Bibel, Zeichnungen und Einstellungen als JSON-Datei. Auf dem iPad landet die Datei ueber das Teilen-Blatt, z. B. in "Dateien".',
    }),
    h(
      'div',
      { class: 'btn-row', style: { marginTop: '12px' } },
      button(
        'Export',
        async () => {
          downloadBackup(await buildBackup());
          toast('Backup erstellt');
        },
        { class: 'btn btn--primary' },
      ),
      button('Import (ergänzen)', () => void importBackup('merge')),
      button('Import (ersetzen)', () => void importBackup('replace'), { class: 'btn btn--danger' }),
    ),
  );

  async function importBackup(mode: 'merge' | 'replace'): Promise<void> {
    const text = await pickJsonFile();
    if (text === null) return;
    try {
      const file = parseBackup(text);
      if (mode === 'replace' && !confirm('Alle vorhandenen Daten ersetzen? Das laesst sich nicht rueckgaengig machen.')) {
        return;
      }
      const result = await restoreBackup(file, mode);
      toast(
        `${result.songs} Songs, ${result.releases} Releases, ${result.setlists} Setlists, ` +
          `${result.boards} Flächen eingelesen`,
      );
      settings = await getSettings();
      setAppearance({ ...settings, customImage: settings.customBackground ?? null });
      applyTheme(settings.themeId);
      refresh();
    } catch (err) {
      alert(err instanceof Error ? err.message : String(err));
    }
  }

  /* ------------------------------------------------------------ Info */

  const infoCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Über' }),
    updateControl(),
    h('p', {
      class: 'hint',
      text: authProvider().authenticated
        ? 'Die Daten liegen in der Cloud (Firebase) und zusaetzlich auf diesem Geraet. Offline Geaendertes wird nachgereicht, sobald wieder Netz da ist.'
        : 'Alle Daten liegen ausschliesslich auf diesem Geraet (IndexedDB). Es gibt kein Backend und keine Verbindung nach aussen.',
    }),
    h('p', {
      class: 'hint',
      text: 'Zum Homescreen hinzufuegen: In Safari auf Teilen tippen, dann "Zum Home-Bildschirm". Danach laeuft die App im Vollbild und offline.',
    }),
    h('p', {
      class: 'hint',
      text: 'Zeichnen: Der Knopf am rechten Rand oeffnet die Werkzeuge. Schneller geht es mit dem Stift: Spitze aufsetzen und eine halbe Sekunde liegen lassen, dann geht die Palette an dieser Stelle auf. Solange ein Werkzeug gewaehlt ist, zeichnet der Pencil auf jedem Bildschirm, waehrend der Finger weiter scrollt und bedient. "Fertig" gibt die Textfelder wieder frei, dann schreibt der Pencil per Scribble.',
    }),
  );

  /* ------------------------------------------------------- Wartung */

  /**
   * Schalter fuer den Wartungsbereich.
   *
   * Bewusst so benannt und beschrieben, wie es ist: er blendet einen Bereich
   * ein, er schuetzt nichts. In einer App ohne Server und ohne Anmeldung
   * waere alles andere eine Behauptung, die nicht traegt.
   */
  const unlockBtn = button('', () => toggleMaintenance(), { class: 'btn' });

  function paintUnlock(): void {
    const on = isMaintenanceUnlocked();
    unlockBtn.textContent = on ? 'Wartungsbereich ausblenden' : 'Wartungsbereich einblenden';
  }

  function toggleMaintenance(): void {
    const next = !isMaintenanceUnlocked();
    setMaintenanceUnlocked(next);
    paintUnlock();
    toast(next ? 'Wartungsbereich eingeblendet' : 'Wartungsbereich ausgeblendet');
    if (next) location.assign(appUrl('system'));
  }
  paintUnlock();

  const maintenanceCard = h(
    'section',
    { class: 'card' },
    h('h2', { class: 'card__title', text: 'Wartung' }),
    h('p', {
      class: 'hint',
      text: 'Zeigt Systemangaben, Zaehlungen und Werkzeuge zum Aufraeumen. Fuer den Alltag nicht noetig.',
    }),
    h('div', { class: 'btn-row' }, unlockBtn),
    h('p', {
      class: 'hint',
      text: 'Ehrlich gesagt: Das ist ein Sichtbarkeits-Schalter, keine Sicherheitsgrenze. Solange alle Daten auf diesem Geraet liegen, kommt jeder daran, der das Geraet hat. Rollen und Rechte sind im Code aber schon getrennt, sodass eine spaetere Anmeldung nur eingehaengt werden muss.',
    }),
  );

  /* ------------------------------------------------------- Konto */

  /**
   * Mit Anmeldung: wer ist angemeldet, Abmelden, und fuer Administratoren
   * der Weg in die Wartung. Der lokale Sichtbarkeits-Schalter entfaellt,
   * dann entscheidet die Rolle.
   */
  const provider = authProvider();
  let accountCard: HTMLElement | null = null;
  if (provider.authenticated) {
    const [me, isAdmin] = await Promise.all([currentUser(), can('admin.view')]);
    // Offene Anfragen gleich am Knopf zeigen, sonst bleiben sie unbemerkt.
    const requests = isAdmin ? (await listUsers()).filter((u) => !u.active && !u.blocked).length : 0;
    accountCard = h(
      'section',
      { class: 'card' },
      h('h2', { class: 'card__title', text: 'Konto' }),
      h('p', { text: `${me?.name ?? ''} · ${provider.email?.() ?? ''}` }),
      h('p', { class: 'hint', text: `Rolle: ${me ? ROLE_LABELS[me.role] : '–'}` }),
      h(
        'div',
        { class: 'btn-row' },
        isAdmin
          ? button(requests > 0 ? `Admin · ${requests} neue Anfrage${requests === 1 ? '' : 'n'}` : 'Admin', () => location.assign(appUrl('system')), {
              class: requests > 0 ? 'btn btn--primary' : 'btn',
            })
          : null,
        provider.signOut
          ? button('Abmelden', () => void provider.signOut?.(), { class: 'btn btn--danger' })
          : null,
      ),
    );
  }

  const meta = SECTIONS.find((sec) => sec.id === section);
  const cards: Record<Exclude<SectionId, 'rechtliches'>, HTMLElement[]> = {
    vibe: [themeCard, backgroundCard, colorCard],
    darstellung: [displayCard],
    konto: [accountCard ?? maintenanceCard],
    speicher: section === 'speicher' ? [await storageCard(() => refresh())] : [],
    daten: [backupCard],
    hilfe: [infoCard],
  };
  return {
    title: section === 'konto' && !accountCard ? 'Wartung' : meta?.title ?? 'Einstellungen',
    back: '#/settings',
    body: h('div', {}, ...cards[section]),
    cleanup: () => window.removeEventListener('leadsheet:theme', onTheme),
  };
}
